import { useAppForm } from '@massilia/features'
import { setAccessToken, useLoginWithEmailMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { identificationLoginSchema } from '@/lib/schemas/'
import { useAuthStore } from '@/stores/auth.store'

import { AuthErrorComponent } from './auth-error-component'

interface IdentificationLoginComponentProps {
  prefilledEmail?: string
}

export function IdentificationLoginComponent({
  prefilledEmail,
}: IdentificationLoginComponentProps) {
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: loginWithEmail } = useLoginWithEmailMutation()
  const { updateAuth } = useAuthStore()
  const navigate = useNavigate()

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const user = await loginWithEmail(value)

        if (!user) {
          setSubmitError('Une erreur est survenue. Veuillez réessayer.')
          return
        }

        setAccessToken(user.accessToken)
        updateAuth({
          isAuthenticated: true,
          user: {
            email: user.email,
            firstName: user.firstName,
            id: user.id,
            lastName: user.lastName,
          },
        })
        void navigate({ to: '/' })
      } catch (error) {
        setSubmitError(
          resolveAuthErrorMessage(error, {
            credentialsMessage: 'Adresse email ou mot de passe incorrect.',
          })
        )
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          identificationLoginSchema
        ),
    },
  })

  useEffect(() => {
    if (!prefilledEmail) {
      return
    }

    form.setFieldValue('email', prefilledEmail)
  }, [form, prefilledEmail])

  return (
    <div className="w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-6">
          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField label="Adresse email" placeholder="email@example.com" />
            )}
          />
          <form.AppField
            name="password"
            children={(field) => (
              <field.PasswordField label="Mot de passe" placeholder="Mot de passe" />
            )}
          />
          <div className="flex w-full justify-start">
            <Link to="/mot-de-passe-oublie">
              <div className="text-brand-700 p-0 text-sm font-semibold underline hover:cursor-pointer">
                J&apos;ai oublié mon mot de passe
              </div>
            </Link>
          </div>
          {submitError && <AuthErrorComponent message={submitError} />}
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                className="bg-brand-600 hover:bg-brand-800 px-8 py-2 text-white"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? 'Connexion en cours...' : 'Se connecter'}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  )
}
